import type { SuccessResponseJSON } from 'openapi-typescript-helpers';
import type { IdentityUser } from '@/common/types/user';
import type { IdentitySchema, PaymentSchema } from '$api-clients/generated';

/**
 * Max number of IDs to send in a single identity `ids` query param. Identity
 * supports comma-separated `ids`, but a single request with hundreds of IDs
 * blows past the proxy's header-size limit and fails with a 431, so callers
 * chunk their lookups by this size (see `fetchUsersAndTeamsMaps` in `./helpers`
 * and `useChunkedUsers`/`useChunkedTeams` in `./components/useChunkedIdentity`).
 */
export const IDS_CHUNK_SIZE = 100;

export type UserDisplayMap = Record<string, IdentityUser>;
export type TeamDisplayMap = Record<
  string,
  SuccessResponseJSON<IdentitySchema.paths['/teams']['get']>['data'][number]
>;
export type ReportsRole =
  PaymentSchema.paths['/reporting/config']['get']['responses']['200']['content']['application/json']['highestRole'];

/** Query params type for GET /reporting/summary */
export type ReportingSummaryQuery =
  PaymentSchema.paths['/reporting/summary']['get']['parameters']['query'];

/** Row type for /reporting/summary results */
export type ReportingSummaryResult =
  PaymentSchema.paths['/reporting/summary']['get']['responses']['200']['content']['application/json']['results'][number];

/** Last 365 days reporting period (rolling window). */
export const REPORTING_PERIOD_L365 = 'L365' as const;

/** Custom date range reporting period. */
export const REPORTING_PERIOD_CUSTOM = 'CUSTOM' as const;

/** All time reporting period — from the user's earliest closingDateStart through today. */
export const REPORTING_PERIOD_ALL_TIME = 'ALL_TIME' as const;

export type ReportingPeriod =
  | number
  | typeof REPORTING_PERIOD_L365
  | typeof REPORTING_PERIOD_CUSTOM
  | typeof REPORTING_PERIOD_ALL_TIME;

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
